import { Box } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { INK } from "../../theme/tokens";
import type { TeamNurse } from "../../utils/models";
import { JumpingDots } from "../UIToolkit/Animations/JumpingDots";
import { EditableSelectCell } from "../UIToolkit/Cells/EditableSelectCell";
import { TooltipTextCell } from "../UIToolkit/Cells/TooltipTextCell/TooltipTextCell";
import type { DropdownItem } from "../UIToolkit/Inputs/SystemDropDown";
import type { SystemGridColDef } from "../UIToolkit/Tables/SystemDataGrid/types";
import { HolidayDayPartLabel } from "./Holidays/HolidayDayPartLabel";
import { HolidayGroupChip } from "./Holidays/HolidayGroupChip";
import { HOLIDAY_NONE, holidayGroupColor, holidayGroupLabel } from "./Holidays/holidayFormat";
import { TEAM_HOLIDAY_DAY_PART_FIELD, TEAM_HOLIDAY_GROUP_FIELD } from "./teamColumnVisibility";
import type { TeamHolidayData } from "./useTeamFilters";

/** Each holiday column's width floor: "Not assigned" as a chip plus the hover pencil. */
const HOLIDAY_COLUMN_MIN_WIDTH = 150;
const NO_VALUE = "—";

const centered = { display: "flex", alignItems: "center", height: "100%", minWidth: 0 } as const;

type Args = {
	holiday: TeamHolidayData;
	/** Read-only admins see the group without a way to change it. */
	canEdit: boolean;
	/** The scheduler picked another group for a row — opens the group change popup, never the profile. */
	onChangeGroup: (nurse: TeamNurse, groupId: string | null) => void;
};

/** The Team grid's "Holiday group" (a dropdown) and "Holiday day part" columns. Hidden until picked in Columns. */
export function useTeamHolidayColumns({ holiday, canEdit, onChangeGroup }: Args): SystemGridColDef<TeamNurse>[] {
	const { t } = useTranslation();
	const { groups, notSetUp, byNurseId, isError } = holiday;

	// Only read once the groups have loaded — a cell shows the placeholder until then.
	const groupItems = useMemo<DropdownItem<string>[]>(
		() =>
			groups === undefined
				? []
				: [
						...groups.map((group) => ({ value: group.id, label: holidayGroupLabel(t, group), color: holidayGroupColor(groups, group.id).dot })),
						{ value: HOLIDAY_NONE, label: t("holidays.group.notAssigned") },
					],
		[groups, t],
	);

	return useMemo(() => {
		// No groups yet: nothing to show, so the columns aren't offered.
		if (notSetUp) return [];
		/**
		 * The cell while there is no person to show: a failure says so, loading shows dots, and someone the
		 * loaded list doesn't have (not on this department's staff) gets a dash that says why.
		 */
		const placeholder = (dataTestId: string) => {
			if (isError) return <TooltipTextCell text={NO_VALUE} tooltip={t("holidays.cell.loadError")} dataTestId={dataTestId} />;
			if (byNurseId !== null && groups !== undefined) {
				return <TooltipTextCell text={NO_VALUE} tooltip={t("holidays.profile.notInDepartment")} dataTestId={dataTestId} />;
			}
			return (
				<Box sx={centered} data-testid={`${dataTestId}-loading`}>
					<JumpingDots />
				</Box>
			);
		};

		return [
			{
				field: TEAM_HOLIDAY_GROUP_FIELD,
				headerName: t("team.column.holiday_group"),
				flex: 12,
				minWidth: HOLIDAY_COLUMN_MIN_WIDTH,
				sortable: false,
				renderCell: ({ row }) => {
					const person = byNurseId?.get(row.id);
					if (person === undefined || groups === undefined) return placeholder(`team-holiday-group-${row.id}`);
					return (
						<EditableSelectCell<string>
							value={person.groupId ?? HOLIDAY_NONE}
							items={groupItems}
							display={<HolidayGroupChip groups={groups} groupId={person.groupId} />}
							onChange={(next) => onChangeGroup(row, next === HOLIDAY_NONE ? null : next)}
							label={t("team.column.holiday_group")}
							disabled={!canEdit}
							dataTestId={`team-holiday-group-${row.id}`}
						/>
					);
				},
			},
			{
				field: TEAM_HOLIDAY_DAY_PART_FIELD,
				headerName: t("team.column.holiday_day_part"),
				flex: 12,
				minWidth: HOLIDAY_COLUMN_MIN_WIDTH,
				sortable: false,
				renderCell: ({ row }) => {
					const person = byNurseId?.get(row.id);
					if (person === undefined) return placeholder(`team-holiday-day-part-${row.id}`);
					return (
						<Box sx={centered}>
							{person.groupId === null ? (
								<Box component="span" sx={{ color: INK.DISABLED }} data-testid={`team-holiday-day-part-${row.id}`}>
									{NO_VALUE}
								</Box>
							) : (
								<HolidayDayPartLabel dayPart={person.dayPart} dataTestId={`team-holiday-day-part-${row.id}`} />
							)}
						</Box>
					);
				},
			},
		];
	}, [t, notSetUp, byNurseId, groups, groupItems, isError, canEdit, onChangeGroup]);
}
